import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable,of, Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { shareReplay, tap, catchError, distinctUntilChanged, filter, takeUntil, debounceTime, switchMap, finalize } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class NotificationService implements OnDestroy{
    private readonly notificationsSubject = new BehaviorSubject<any[]>([]);
    public notifications$ = this.notificationsSubject.asObservable();
    private refreshInterval: any;
    private autoRefreshEnabled = false;
    private lastLoadTime = 0;
    private currentEmployeeNumber: number | null = null;
    private readonly CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache
    private readonly destroy$ = new Subject<void>();
    private isInitialized = false;
    private notificationsLoadInFlight = false;

    constructor(private readonly authService: AuthService) {
        this.setupUserChangeHandling();
        this.initializeNotifications();

        // Enable intelligent auto-refresh after a delay (10 minutes interval)
        setTimeout(() => {
            this.enableIntelligentAutoRefresh();
        }, 30000); // Start auto-refresh after 30 seconds
    }

    private initializeNotifications(): void {
        // Try to initialize notifications immediately if user is already available
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const userData = JSON.parse(storedUser);
                if (userData?.credentials?.EmployeeNumber) {
                    this.currentEmployeeNumber = userData.credentials.EmployeeNumber;
                    this.loadNotifications();
                    this.isInitialized = true;
                }
            } catch (error) {
                console.error('NotificationService: Error parsing stored user data:', error);
            }
        }
    }

    private setupUserChangeHandling(): void {
        // Subscribe to user changes and clear cache/refresh when different user
        this.authService.currentUser$.pipe(
            takeUntil(this.destroy$),
            filter(user => {
                return !!user?.credentials?.EmployeeNumber;
            }),
            distinctUntilChanged((prev, curr) => {
                const prevEmployee = prev?.credentials?.EmployeeNumber;
                const currEmployee = curr?.credentials?.EmployeeNumber;
                const isSameEmployee = prevEmployee === currEmployee;

                return isSameEmployee;
            }),
            debounceTime(500), // Add debouncing to prevent rapid successive calls
            switchMap(user => {
                // Clear cache and reset state for new user
                this.lastLoadTime = 0;
                this.notificationsSubject.next([]);

                // Update current employee number
                this.currentEmployeeNumber = user.credentials.EmployeeNumber;

                // Load notifications for the new user immediately
                this.loadNotifications();
                this.isInitialized = true;

                // Return observable that completes immediately
                return of(null);
            })
        ).subscribe();

        // Subscribe to logout events separately to handle user switches
        this.authService.logoutEvent.pipe(
            takeUntil(this.destroy$),
            debounceTime(300) // Add debouncing for logout events too
        ).subscribe(() => {
            this.handleUserChange();
        });
    }

    private handleUserChange() {
        // Clear cache and reset state
        this.lastLoadTime = 0;
        this.currentEmployeeNumber = null;
        this.notificationsSubject.next([]);
        this.isInitialized = false;

        // Disable auto-refresh temporarily
        this.disableAutoRefresh();

        // Re-enable after user is set
        setTimeout(() => {
            if (this.currentEmployeeNumber) {
                this.enableIntelligentAutoRefresh();
            }
        }, 1000);
    }

    private loadNotifications(forceRefresh = false) {
        if (!this.currentEmployeeNumber) {
            return;
        }

        if (this.notificationsLoadInFlight) {
            return;
        }

        const now = Date.now();

        // Avoid frequent API calls - use cache for 2 minutes
        if (!forceRefresh && now - this.lastLoadTime < this.CACHE_DURATION) {
            return;
        }

        this.notificationsLoadInFlight = true;

        this.authService.getNotifications(forceRefresh).pipe(
            tap(notifications => {
                if (notifications && Array.isArray(notifications)) {
                    this.notificationsSubject.next(notifications);
                    this.lastLoadTime = now;
                } else {
                    this.notificationsSubject.next([]);
                    this.lastLoadTime = now;
                }
            }),
            catchError(() => {
                this.notificationsSubject.next([]);
                return of([]);
            }),
            finalize(() => {
                this.notificationsLoadInFlight = false;
            })
        ).subscribe();
    }

    public getNotifications(): Observable<any[]> {
        return this.notifications$;
    }

    public refreshNotifications() {
        this.lastLoadTime = 0;
        this.loadNotifications(true);
    }

    public enableAutoRefresh() {
        if (this.autoRefreshEnabled || !this.currentEmployeeNumber) {
            return;
        }

        this.autoRefreshEnabled = true;

        // Refresh notifications every 5 minutes when auto-refresh is enabled
        this.refreshInterval = setInterval(() => {
            if (this.currentEmployeeNumber) {
                this.loadNotifications();
            }
        }, 5 * 60 * 1000); // 5 minutes
    }

    public disableAutoRefresh() {
        this.autoRefreshEnabled = false;

        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
    }

    private enableIntelligentAutoRefresh() {
        if (this.autoRefreshEnabled || !this.currentEmployeeNumber) {
            return;
        }

        this.autoRefreshEnabled = true;

        // Refresh notifications every 10 minutes silently
        this.refreshInterval = setInterval(() => {
            if (this.currentEmployeeNumber) {
                this.loadNotifications();
            }
        }, 10 * 60 * 1000); // 10 minutes
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.disableAutoRefresh();
    }
} 